import React, { useState, useEffect, useRef } from 'react';
import { Clock, Calendar, Save, User, AlertCircle, CheckCircle, ChevronDown, Check, X } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { TYPES_FACTURATION, CATEGORIES_TEMPS } from '../data/mockData';
import { getWeekNumber, formatDateForWeekInput, getCurrentWeek, weekInputToDate, formatDateForAPI } from '../utils/dateUtils';

const SaisieTemps = () => {
  const { state, actions } = useApp();
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [selectedProjet, setSelectedProjet] = useState('');
  const [selectedPersonnes, setSelectedPersonnes] = useState([]);
  const [tempsDataByPersonne, setTempsDataByPersonne] = useState({});
  const [allTempsConsommes, setAllTempsConsommes] = useState([]);
  const [existingTempsbyPersonne, setExistingTempsByPersonne] = useState({});

  const [errors, setErrors] = useState({});
  const [saved, setSaved] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Modal state for comment display
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);

  // Filter states for historique
  const [selectedPersonneFilter, setSelectedPersonneFilter] = useState('');
  const [selectedProjetFilter, setSelectedProjetFilter] = useState('');
  const [selectedFacturableFilter, setSelectedFacturableFilter] = useState('');

  const projet = selectedProjet ? state.projets?.data?.find(p => p.id === parseInt(selectedProjet)) : null;

  // Handle comment click
  const handleCommentClick = (temps) => {
    if (temps.commentaire && temps.commentaire.trim()) {
      setSelectedComment(temps);
      setShowCommentModal(true);
    }
  };

  // Close modal
  const closeCommentModal = () => {
    setShowCommentModal(false);
    setSelectedComment(null);
  };

  // Handle keyboard events for modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showCommentModal) {
        closeCommentModal();
      }
    };

    if (showCommentModal) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showCommentModal]);

  // Load all temps consommés when component mounts
  useEffect(() => {
    const loadAllTemps = async () => {
      try {
        const result = await actions.loadAllTempsConsommes({ limit: 100 });
        if (state.tempsConsommes?.data) {
          setAllTempsConsommes(state.tempsConsommes.data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des temps consommés:', error);
      }
    };

    loadAllTemps();
  }, []);

  // Update local state when global state changes
  useEffect(() => {
    if (state.tempsConsommes?.data) {
      setAllTempsConsommes(state.tempsConsommes.data);
    }
  }, [state.tempsConsommes]);

  // Reset form when project changes
  useEffect(() => {
    if (selectedProjet && selectedPersonnes.length > 0) {
      setTempsDataByPersonne(prev => {
        const newTempsData = {};
        selectedPersonnes.forEach(personneId => {
          newTempsData[personneId] = {
            jours_consommes: '',
            facturable: projet?.facturable ?? true,
            commentaire: '',
            phase: 'build'
          };
        });
        return newTempsData;
      });
    }
  }, [selectedProjet, projet, selectedPersonnes]);

  // Initialize temps data when personnes selection changes
  useEffect(() => {
    setTempsDataByPersonne(prev => {
      const newTempsData = { ...prev };
      
      // Add data for newly selected personnes
      selectedPersonnes.forEach(personneId => {
        if (!newTempsData[personneId]) {
          newTempsData[personneId] = {
            jours_consommes: '',
            facturable: projet?.facturable ?? true,
            commentaire: '',
            phase: 'build'
          };
        }
      });
      
      // Remove data for unselected personnes
      Object.keys(newTempsData).forEach(personneId => {
        if (!selectedPersonnes.includes(personneId)) {
          delete newTempsData[personneId];
        }
      });
      
      return newTempsData;
    });
  }, [selectedPersonnes, projet]);

  const handleTempsChange = (personneId, fieldName, value) => {
    setTempsDataByPersonne(prev => ({
      ...prev,
      [personneId]: {
        ...prev[personneId],
        [fieldName]: value
      }
    }));
    
    // Clear related errors
    const errorKey = `${personneId}_${fieldName}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
    setSaved(false);
  };

  const togglePersonne = (personneId) => {
    setSelectedPersonnes(prev => {
      const isSelected = prev.includes(personneId);
      const newSelection = isSelected 
        ? prev.filter(id => id !== personneId)
        : [...prev, personneId];
      return newSelection;
    });
    setErrors(prev => ({ ...prev, personnes: '' }));
  };

  const toggleSelectAll = () => {
    const activePersonnes = state.personnes?.data?.filter(p => p.actif) || [];
    if (selectedPersonnes.length === activePersonnes.length) {
      setSelectedPersonnes([]);
    } else {
      setSelectedPersonnes(activePersonnes.map(p => p.id.toString()));
    }
    setErrors(prev => ({ ...prev, personnes: '' }));
  };

  // Function to get existing temps for a person and week
  const getExistingTempsForPersonneWeek = (personneId, week) => {
    if (!allTempsConsommes || !week) return [];
    
    const weekDate = weekInputToDate(week);
    const apiWeekFormat = formatDateForAPI(weekDate);
    
    return allTempsConsommes.filter(temps => 
      temps.id_personne === parseInt(personneId) && 
      temps.semaine === apiWeekFormat
    );
  };

  // Function to filter temps consommés based on filters
  const filteredTempsConsommes = allTempsConsommes.filter(temps => {
    // Filter by personne
    if (selectedPersonneFilter && temps.id_personne !== parseInt(selectedPersonneFilter)) {
      return false;
    }
    
    // Filter by projet
    if (selectedProjetFilter && temps.id_projet !== parseInt(selectedProjetFilter)) {
      return false;
    }
    
    // Filter by facturable
    if (selectedFacturableFilter !== '') {
      if (selectedFacturableFilter === 'facturable' && !temps.facturable) {
        return false;
      }
      if (selectedFacturableFilter === 'non-facturable' && temps.facturable) {
        return false;
      }
    }
    
    return true;
  });

  // Reset filters function
  const resetFilters = () => {
    setSelectedPersonneFilter('');
    setSelectedProjetFilter('');
    setSelectedFacturableFilter('');
  };

  // Update existing temps when week or personnes selection changes
  useEffect(() => {
    if (selectedWeek && selectedPersonnes.length > 0) {
      const existingTemps = {};
      selectedPersonnes.forEach(personneId => {
        existingTemps[personneId] = getExistingTempsForPersonneWeek(personneId, selectedWeek);
      });
      setExistingTempsByPersonne(existingTemps);
    } else {
      setExistingTempsByPersonne({});
    }
  }, [selectedWeek, selectedPersonnes, allTempsConsommes]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);



  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedProjet) {
      newErrors.projet = 'Sélectionnez un projet';
    }
    
    if (selectedPersonnes.length === 0) {
      newErrors.personnes = 'Sélectionnez au moins une personne';
    }
    
    if (!selectedWeek) {
      newErrors.semaine = 'Sélectionnez une semaine';
    }
    
    // Validate each personne's data
    selectedPersonnes.forEach(personneId => {
      const tempsData = tempsDataByPersonne[personneId];
      if (!tempsData) return;
      
      const joursConsommes = parseFloat(tempsData.jours_consommes);
      if (!tempsData.jours_consommes || joursConsommes <= 0) {
        newErrors[`${personneId}_jours_consommes`] = 'Les jours consommés doivent être supérieurs à 0';
      } else if (joursConsommes > 5) {
        newErrors[`${personneId}_jours_consommes`] = 'Les jours consommés ne peuvent pas dépasser 5 par semaine';
      }
      
      if (!tempsData.commentaire.trim()) {
        newErrors[`${personneId}_commentaire`] = 'Le commentaire est requis';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveTemps = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Convert week format to Monday date for API
      const weekDate = weekInputToDate(selectedWeek);
      const apiWeekFormat = formatDateForAPI(weekDate);
      
      // Save temps consommés for each selected personne
      const savePromises = selectedPersonnes.map(personneId => {
        const tempsData = tempsDataByPersonne[personneId];
        return actions.saveTempsConsomme({
          id_projet: parseInt(selectedProjet),
          id_personne: parseInt(personneId),
          semaine: apiWeekFormat,
          jours_consommes: parseFloat(tempsData.jours_consommes),
          facturable: tempsData.facturable,
          commentaire: tempsData.commentaire,
          phase: tempsData.phase
        });
      });

      await Promise.all(savePromises);

      // Reload the chronological list
      await actions.loadAllTempsConsommes({ limit: 100 });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };





  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Saisie des temps consommés</h1>
          <p className="mt-2 text-sm text-gray-700">
            Enregistrez vos temps de travail réels par projet et par semaine
          </p>
        </div>
        {saved && (
          <div className="mt-4 sm:mt-0 flex items-center text-green-600">
            <CheckCircle className="h-5 w-5 mr-2" />
            <span className="text-sm font-medium">Sauvegardé avec succès</span>
          </div>
        )}
      </div>

      {/* Sélection du contexte */}
      <div className="mt-8 card">
        <div className="flex items-center mb-6">
          <Calendar className="h-6 w-6 text-primary-600 mr-3" />
          <h2 className="text-lg font-semibold text-gray-900">Contexte de saisie</h2>
        </div>
        
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="form-label">Semaine *</label>
            <input
              type="week"
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              className={`form-input ${errors.semaine ? 'border-red-500' : ''}`}
            />
            {errors.semaine && (
              <p className="mt-1 text-sm text-red-600">{errors.semaine}</p>
            )}
          </div>
          
          <div>
            <label className="form-label">Projet *</label>
            <select 
              value={selectedProjet}
              onChange={(e) => setSelectedProjet(e.target.value)}
              className={`form-input ${errors.projet ? 'border-red-500' : ''}`}
            >
              <option value="">Sélectionnez un projet</option>
              {state.projets?.data?.map(projet => (
                <option key={projet.id} value={projet.id}>
                  {projet.nom} {!projet.facturable && '(Non facturable)'}
                </option>
              ))}
            </select>
            {errors.projet && (
              <p className="mt-1 text-sm text-red-600">{errors.projet}</p>
            )}
            {projet && (
              <p className={`mt-1 text-sm ${projet.facturable ? 'text-green-600' : 'text-gray-600'}`}>
                Projet {projet.facturable ? 'facturable' : 'non facturable'}
              </p>
            )}
          </div>

          <div className="relative" ref={dropdownRef}>
            <label className="form-label">Personnes *</label>
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`form-input w-full text-left flex items-center justify-between ${errors.personnes ? 'border-red-500' : ''}`}
            >
              <span className="text-gray-700">
                {selectedPersonnes.length === 0
                  ? 'Sélectionnez des personnes'
                  : `${selectedPersonnes.length} personne(s) sélectionnée(s)`
                }
              </span>
              <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {/* Select All option */}
                <div 
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                  onClick={toggleSelectAll}
                >
                  <div className="flex items-center justify-center w-4 h-4 border border-gray-300 rounded mr-3">
                    {selectedPersonnes.length === state.personnes?.data?.filter(p => p.actif).length && 
                     selectedPersonnes.length > 0 && (
                      <Check className="h-3 w-3 text-primary-600" />
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    Tout sélectionner
                  </span>
                </div>

                {/* Individual options */}
                {state.personnes?.data?.filter(p => p.actif).map(personne => (
                  <div 
                    key={personne.id}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    onClick={() => togglePersonne(personne.id.toString())}
                  >
                    <div className="flex items-center justify-center w-4 h-4 border border-gray-300 rounded mr-3">
                      {selectedPersonnes.includes(personne.id.toString()) && (
                        <Check className="h-3 w-3 text-primary-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <span className="text-sm text-gray-900">
                        {personne.prenom} {personne.nom}
                      </span>
                      <span className="text-xs text-gray-500 ml-2">
                        ({personne.role})
                      </span>
                    </div>
                  </div>
                ))}

                {/* Empty state */}
                {(!state.personnes?.data?.filter(p => p.actif) || state.personnes.data.filter(p => p.actif).length === 0) && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Aucune personne active disponible
                  </div>
                )}
              </div>
            )}

            {errors.personnes && (
              <p className="mt-1 text-sm text-red-600">{errors.personnes}</p>
            )}
            {selectedPersonnes.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {selectedPersonnes.map(personneId => {
                  const personne = state.personnes?.data?.find(p => p.id.toString() === personneId);
                  return personne ? (
                    <span 
                      key={personneId}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                    >
                      {personne.prenom} {personne.nom}
                      <button
                        type="button"
                        onClick={() => togglePersonne(personneId)}
                        className="ml-1 h-3 w-3 rounded-full hover:bg-primary-200 flex items-center justify-center"
                      >
                        ×
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </div>
        </div>
      </div>



      {/* Temps consommés */}
      <div className="mt-8 card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Temps consommés</h3>
          <button 
            onClick={handleSaveTemps}
            disabled={!selectedProjet || selectedPersonnes.length === 0 || !selectedWeek}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4 mr-2" />
            Sauvegarder tous les temps
          </button>
        </div>
        
        {selectedPersonnes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Sélectionnez des personnes pour saisir leurs temps</p>
          </div>
        ) : (
          <div className="space-y-6">
            {selectedPersonnes.map((personneId, index) => {
              const personne = state.personnes?.data?.find(p => p.id === parseInt(personneId));
              const tempsData = tempsDataByPersonne[personneId] || {};
              
              return (
                <div key={personneId} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center mb-4">
                    <User className="h-5 w-5 text-primary-600 mr-2" />
                    <h4 className="text-lg font-medium text-gray-900">
                      {personne?.prenom} {personne?.nom} ({personne?.role})
                    </h4>
                    <span className="ml-auto text-sm text-gray-600">
                      Taux: {personne?.taux_journalier}€/jour
                    </span>
                  </div>

                  {/* Affichage des temps existants pour cette personne et cette semaine */}
                  {existingTempsbyPersonne[personneId] && existingTempsbyPersonne[personneId].length > 0 && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <h5 className="text-sm font-medium text-blue-800 mb-2">
                        Temps déjà saisis pour cette semaine :
                      </h5>
                      <div className="space-y-2">
                        {existingTempsbyPersonne[personneId].map((temps, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <div className="flex items-center space-x-2">
                              <span className="text-blue-700 font-medium">
                                {temps.projet?.nom}
                              </span>
                              <span className="text-blue-600">
                                - {temps.jours_consommes} jour{temps.jours_consommes > 1 ? 's' : ''}
                              </span>
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                temps.facturable 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {temps.facturable ? 'Facturable' : 'Non fact.'}
                              </span>
                              {temps.phase && (
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                  CATEGORIES_TEMPS.find(cat => cat.value === temps.phase)?.color || 'bg-gray-100 text-gray-600'
                                }`}>
                                  {CATEGORIES_TEMPS.find(cat => cat.value === temps.phase)?.label || temps.phase}
                                </span>
                              )}
                            </div>
                            {temps.commentaire && (
                              <button 
                                onClick={() => handleCommentClick(temps)}
                                className="text-blue-600 hover:text-blue-800 text-xs underline"
                                title="Voir le commentaire"
                              >
                                Commentaire
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 pt-2 border-t border-blue-200">
                        <span className="text-sm font-medium text-blue-800">
                          Total semaine : {existingTempsbyPersonne[personneId].reduce((total, temps) => total + parseFloat(temps.jours_consommes), 0).toFixed(1)} jours
                        </span>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div>
                      <label className="form-label">Jours consommés *</label>
                      <input
                        type="number"
                        value={tempsData.jours_consommes || ''}
                        onChange={(e) => handleTempsChange(personneId, 'jours_consommes', e.target.value)}
                        step="0.5"
                        min="0"
                        max="5"
                        className={`form-input ${errors[`${personneId}_jours_consommes`] ? 'border-red-500' : ''}`}
                        placeholder="0.0"
                      />
                      {errors[`${personneId}_jours_consommes`] && (
                        <p className="mt-1 text-sm text-red-600">{errors[`${personneId}_jours_consommes`]}</p>
                      )}
                    </div>

                    <div>
                      <label className="form-label">Phase *</label>
                      <select
                        value={tempsData.phase || 'build'}
                        onChange={(e) => handleTempsChange(personneId, 'phase', e.target.value)}
                        className="form-input"
                      >
                        {CATEGORIES_TEMPS.map((categorie) => (
                          <option key={categorie.value} value={categorie.value}>
                            {categorie.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="form-label">Type de facturation</label>
                      <select
                        value={tempsData.facturable}
                        onChange={(e) => handleTempsChange(personneId, 'facturable', e.target.value === 'true')}
                        className="form-input"
                      >
                        {TYPES_FACTURATION.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-sm text-gray-500">
                        {tempsData.facturable 
                          ? 'Ce temps sera facturé au client'
                          : 'Temps interne non facturable'
                        }
                      </p>
                    </div>

                    <div>
                      <label className="form-label">Montant estimé</label>
                      <div className="mt-1 p-3 bg-white border border-gray-300 rounded-md">
                        <span className="text-sm font-medium text-gray-900">
                          {personne && tempsData.jours_consommes && tempsData.facturable
                            ? `${(parseFloat(tempsData.jours_consommes) * personne.taux_journalier).toFixed(2)}€`
                            : '0€'
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="form-label">Commentaire *</label>
                    <textarea
                      value={tempsData.commentaire || ''}
                      onChange={(e) => handleTempsChange(personneId, 'commentaire', e.target.value)}
                      className={`form-input ${errors[`${personneId}_commentaire`] ? 'border-red-500' : ''}`}
                      rows="3"
                      placeholder="Décrivez le travail de la semaine..."
                    />
                    {errors[`${personneId}_commentaire`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`${personneId}_commentaire`]}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Alertes */}
      {projet && !projet.facturable && selectedPersonnes.some(personneId => 
        tempsDataByPersonne[personneId]?.facturable
      ) && (
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-400 mr-3 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Attention</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Vous saisissez du temps facturable sur un projet non facturable. 
                Vérifiez que c'est correct ou modifiez le type de facturation pour les personnes concernées.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Historique des temps consommés */}
      <div className="mt-8 card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Clock className="h-6 w-6 text-primary-600 mr-3" />
            <h2 className="text-lg font-semibold text-gray-900">Historique des temps consommés</h2>
          </div>
          {(selectedPersonneFilter || selectedProjetFilter || selectedFacturableFilter) && (
            <button
              onClick={resetFilters}
              className="text-sm text-primary-600 hover:text-primary-800 underline"
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>

        {/* Filtres */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Personne</label>
              <select
                value={selectedPersonneFilter}
                onChange={(e) => setSelectedPersonneFilter(e.target.value)}
                className="form-input w-full"
              >
                <option value="">Toutes les personnes</option>
                {state.personnes?.data?.map(personne => (
                  <option key={personne.id} value={personne.id}>
                    {personne.prenom} {personne.nom} ({personne.role})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Projet</label>
              <select
                value={selectedProjetFilter}
                onChange={(e) => setSelectedProjetFilter(e.target.value)}
                className="form-input w-full"
              >
                <option value="">Tous les projets</option>
                {state.projets?.data?.map(projet => (
                  <option key={projet.id} value={projet.id}>
                    {projet.nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type de facturation</label>
              <select
                value={selectedFacturableFilter}
                onChange={(e) => setSelectedFacturableFilter(e.target.value)}
                className="form-input w-full"
              >
                <option value="">Tous les types</option>
                <option value="facturable">Facturable uniquement</option>
                <option value="non-facturable">Non facturable uniquement</option>
              </select>
            </div>
          </div>
          
          {/* Résumé des filtres actifs */}
          {(selectedPersonneFilter || selectedProjetFilter || selectedFacturableFilter) && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex flex-wrap gap-2">
                {selectedPersonneFilter && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Personne: {state.personnes?.data?.find(p => p.id === parseInt(selectedPersonneFilter))?.prenom} {state.personnes?.data?.find(p => p.id === parseInt(selectedPersonneFilter))?.nom}
                    <button
                      onClick={() => setSelectedPersonneFilter('')}
                      className="ml-1 h-3 w-3 rounded-full hover:bg-blue-200 flex items-center justify-center"
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedProjetFilter && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Projet: {state.projets?.data?.find(p => p.id === parseInt(selectedProjetFilter))?.nom}
                    <button
                      onClick={() => setSelectedProjetFilter('')}
                      className="ml-1 h-3 w-3 rounded-full hover:bg-green-200 flex items-center justify-center"
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedFacturableFilter && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    Type: {selectedFacturableFilter === 'facturable' ? 'Facturable' : 'Non facturable'}
                    <button
                      onClick={() => setSelectedFacturableFilter('')}
                      className="ml-1 h-3 w-3 rounded-full hover:bg-purple-200 flex items-center justify-center"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {allTempsConsommes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Aucun temps consommé enregistré</p>
          </div>
        ) : filteredTempsConsommes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Aucun temps consommé ne correspond aux filtres sélectionnés</p>
            <button 
              onClick={resetFilters}
              className="mt-2 text-sm text-primary-600 hover:text-primary-800 underline"
            >
              Réinitialiser les filtres
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="mb-4 text-sm text-gray-600">
              Affichage de {filteredTempsConsommes.length} résultat{filteredTempsConsommes.length > 1 ? 's' : ''} sur {allTempsConsommes.length} temps consommé{allTempsConsommes.length > 1 ? 's' : ''}
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Semaine
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Personne
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Projet
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Temps consommé
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phase
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commentaire
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTempsConsommes.map((temps, index) => (
                  <tr key={temps.id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(temps.semaine).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {temps.personne?.prenom} {temps.personne?.nom}
                      </div>
                      <div className="text-xs text-gray-500">
                        {temps.personne?.role}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {temps.projet?.nom}
                      </div>
                      <div className="text-xs text-gray-500">
                        {temps.projet?.statut}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="font-medium">{temps.jours_consommes} jour{temps.jours_consommes > 1 ? 's' : ''}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {temps.phase && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          CATEGORIES_TEMPS.find(cat => cat.value === temps.phase)?.color || 'bg-gray-100 text-gray-800'
                        }`}>
                          {CATEGORIES_TEMPS.find(cat => cat.value === temps.phase)?.label || temps.phase}
                        </span>
                      )}
                      {!temps.phase && (
                        <span className="text-xs text-gray-400 italic">Non définie</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        temps.facturable 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {temps.facturable ? 'Facturable' : 'Non facturable'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {temps.commentaire ? (
                        <button 
                          onClick={() => handleCommentClick(temps)} 
                          className="text-left w-full hover:text-primary-600 transition-colors cursor-pointer underline decoration-dotted underline-offset-2"
                          title="Cliquer pour voir le commentaire complet"
                        >
                          {temps.commentaire}
                        </button>
                      ) : (
                        <span className="italic text-gray-400">Aucun commentaire</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Comment Modal */}
      {showCommentModal && selectedComment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeCommentModal}>
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Détails du temps consommé</h3>
              <button 
                onClick={closeCommentModal} 
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Context information */}
            <div className="bg-gray-50 p-4 rounded-md mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Personne</p>
                  <p className="text-sm text-gray-900">
                    {selectedComment.personne?.prenom} {selectedComment.personne?.nom}
                  </p>
                  <p className="text-xs text-gray-500">{selectedComment.personne?.role}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Projet</p>
                  <p className="text-sm text-gray-900">{selectedComment.projet?.nom}</p>
                  <p className="text-xs text-gray-500">{selectedComment.projet?.statut}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Semaine</p>
                  <p className="text-sm text-gray-900">
                    {new Date(selectedComment.semaine).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Temps consommé</p>
                  <p className="text-sm text-gray-900">
                    {selectedComment.jours_consommes} jour{selectedComment.jours_consommes > 1 ? 's' : ''}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedComment.facturable 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedComment.facturable ? 'Facturable' : 'Non facturable'}
                    </span>
                    {selectedComment.phase && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        CATEGORIES_TEMPS.find(cat => cat.value === selectedComment.phase)?.color || 'bg-gray-100 text-gray-800'
                      }`}>
                        {CATEGORIES_TEMPS.find(cat => cat.value === selectedComment.phase)?.label || selectedComment.phase}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Comment content */}
            <div>
              <p className="text-sm font-medium text-gray-600 mb-2">Commentaire</p>
              <div className="bg-white border border-gray-200 rounded-md p-3 max-h-32 overflow-y-auto">
                <p className="text-sm text-gray-800 whitespace-pre-wrap">
                  {selectedComment.commentaire || "Aucun commentaire disponible"}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaisieTemps; 